import { Component, Inject, OnInit } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormArray,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AddFuelSupplierRequest,
  FuelSupplierService,
  UpdateFuelSupplierContactRequest,
  UpdateFuelSupplierRequest,
} from '../../../shared';
import { Contact } from '../../../shared';

import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../../components/shared/confirmation-dialog/confirmation-dialog.component';


@Component({
  standalone: false,
  selector: 'app-fuel-supplier-dialog',
  templateUrl: './fuel-supplier-dialog.component.html',
  styleUrls: ['./fuel-supplier-dialog.component.scss'],
})
export class FuelSupplierDialogComponent implements OnInit {
  newSupplierForm: FormGroup;
  errorMessage?: string;
  dialogTitle: string = 'Add New Fuel Supplier';
  maxRecords: number = 12;
  contactsToDelete: number[] = [];

  constructor(
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<FuelSupplierDialogComponent>,
    private dataService: FuelSupplierService,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: { isEditMode: boolean; fuelSupplierId?: number } | null
  ) {
    if (!this.data) {
      this.data = { isEditMode: false };
    }

    this.newSupplierForm = this.fb.group({
      fuelSupplier: ['', [Validators.required, Validators.maxLength(100), this.noPolishCharsValidator]],
      stationManager: ['', [Validators.required, Validators.maxLength(50), this.noPolishCharsValidator]],
      address: ['', [Validators.maxLength(300), this.noPolishCharsValidator]],
      sapId: ['', [Validators.maxLength(15), this.onlyNumbersValidator]],
      remarks: ['', [Validators.maxLength(300), this.noPolishCharsValidator]],
      media: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    if (this.data && this.data.isEditMode && this.data.fuelSupplierId) {
      this.dialogTitle = 'Edit Fuel Supplier';
      this.loadFuelSupplier(this.data.fuelSupplierId);
    }

    this.newSupplierForm.updateValueAndValidity();
  }

  get mediaArray(): FormArray {
    return this.newSupplierForm.get('media') as FormArray;
  }

  getMediaGroup(index: number): FormGroup {
    return this.mediaArray.at(index) as FormGroup;
  }

  private loadFuelSupplier(fuelSupplierId: number) {
    this.dataService.getFuelSupplierById(fuelSupplierId).subscribe({
      next: (records) => {
        const supplier = this.groupContactsBySupplier(records);
        this.populateForm(supplier);
      },
      error: (err) => console.error('Failed to load fuel supplier data:', err),
    });
  }

  private groupContactsBySupplier(records: any[]): any {
    const supplier = {
      id: records[0].id,
      name: records[0].name,
      stationManager: records[0].stationManager,
      address: records[0].address,
      sapId: records[0].sapId,
      remarks: records[0].remarks,
      contacts: records.map(
        (record) =>
          ({
            id: record.contactId,
            mediaNo: record.contactMediaNo,
            mediaType: record.contactMediaType,
            contactType: record.contactType,
            contactDetails: record.contactDetails,
          } as Contact)
      ),
    };
    return supplier;
  }

  setContactValidators(mediaGroup: FormGroup) {
    const mediaTypeControl = mediaGroup.get('mediaType');
    const contactControl = mediaGroup.get('contactDetails');

    if (mediaTypeControl && contactControl) {
      if (mediaTypeControl.value === 'PHONE') {
        contactControl.setValidators([
          Validators.required,
          Validators.maxLength(100),
          Validators.pattern(/^\+{0,1}[\d ]+$/),
        ]);
      } else if (mediaTypeControl.value === 'EMAIL') {
        contactControl.setValidators([
          Validators.required,
          Validators.maxLength(100),
          this.strictEmailValidator
        ]);
      } else {
        contactControl.setValidators(null);
      }

      contactControl.updateValueAndValidity();
    }
  }

  private strictEmailValidator(control: AbstractControl): ValidationErrors | null {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (control.value && !emailRegex.test(control.value)) {
      return { invalidEmail: true };
    }
    return null;
  }

  private populateForm(supplier: any) {
    this.newSupplierForm.patchValue({
      fuelSupplier: supplier.name,
      stationManager: supplier.stationManager,
      address: supplier.address,
      sapId: supplier.sapId,
      remarks: supplier.remarks,
    });

    const mediaArray = this.newSupplierForm.get('media') as FormArray;
    mediaArray.clear();

    const validContacts = supplier.contacts.filter(
      (contact: Contact) => contact.mediaType !== null && contact.contactDetails !== null
    );

    if (validContacts.length > 0) {
      validContacts.forEach((contact: Contact) => {
        mediaArray.push(
          this.createMediaEntry(contact.id, contact.mediaNo, contact.mediaType, contact.contactType, contact.contactDetails)
        );
      });
    }

    this.updateMediaNumbers();
  }

  private createMediaEntry(
    id: number | undefined = undefined,
    mediaNo: number = 0,
    mediaType: string | null = null,
    contactType: string | null = null,
    contactDetails: string = ''
  ): FormGroup {
    const mediaGroup = this.fb.group({
      id: [id],
      mediaNo: [mediaNo, Validators.required],
      mediaType: [mediaType, Validators.required],
      contactType: [contactType, Validators.required],
      contactDetails: [contactDetails],
    });

    this.setContactValidators(mediaGroup);

    mediaGroup.get('mediaType')?.valueChanges.subscribe(() => {
      this.setContactValidators(mediaGroup);
      this.updateMediaNumbers();
    });

    return mediaGroup;
  }

  onCancel() {
    if (this.data && this.newSupplierForm.dirty) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '350px',
        data: {
          title: 'Attention',
          message: 'Are you sure you want to exit without saving changes?',
          buttons: {
            cancel: 'No',
            confirm: 'Yes',
          },
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'Yes') {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }

  addMedia() {
    if (this.mediaArray.length >= this.maxRecords) {
      this.errorMessage = `Cannot add more than ${this.maxRecords} media records.`;
      return;
    }

    const phoneCount = this.mediaArray.value.filter((item: any) => item.mediaType === 'PHONE').length;
    const emailCount = this.mediaArray.value.filter((item: any) => item.mediaType === 'EMAIL').length;

    if (phoneCount >= 7) {
      this.errorMessage = 'Cannot add more than 6 contacts of type PHONE.';
      return;
    } else if (emailCount >= 7) {
      this.errorMessage = 'Cannot add more than 6 contacts of type EMAIL.';
      return;
    }

    this.errorMessage = '';

    const newMediaEntry = this.createMediaEntry();
    this.mediaArray.push(newMediaEntry);

    this.newSupplierForm.updateValueAndValidity();

    this.updateMediaNumbers();
  }

  removeMedia(index: number) {
    const contact = this.mediaArray.at(index).value;

    if (this.data && this.data.isEditMode && contact.id) {
      this.contactsToDelete.push(contact.id);
    }

    this.mediaArray.removeAt(index);
    this.updateMediaNumbers();
  }

  private onlyNumbersValidator(control: AbstractControl) : ValidationErrors | null {
    const onlyNumbersRegex = /^[0-9]*$/;
    if (!control.value || onlyNumbersRegex.test(control.value)) {
      return null;
    }
    return { invalidChars: true };
  }

  private noPolishCharsValidator(control: AbstractControl): ValidationErrors | null {
    const polishCharsRegex = /[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/;
    if (control.value && polishCharsRegex.test(control.value)) {
      return { polishChars: true };
    }
    return null;
  }

  public updateMediaNumbers() {
    const counts: { PHONE: number; EMAIL: number } = { PHONE: 0, EMAIL: 0 };
    this.errorMessage = '';

    this.mediaArray.controls.forEach((control) => {
      const type = control.get('mediaType')?.value as 'PHONE' | 'EMAIL' | null;

      if (type) {
        if (counts[type] < 6) {
          control.get('mediaNo')?.setValue(counts[type]);
          counts[type]++;
        } else {
          control.get('mediaNo')?.setValue(null);
          this.errorMessage = `Cannot add more than 6 contacts of type ${type}.`;
        }
      } else {
        control.get('mediaNo')?.setValue(null);
      }
    });
  }

  private findInvalidControls() {
    const invalidControls = [];
    const controls = this.newSupplierForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalidControls.push(name);
      }
    }
    return invalidControls;
  }

  saveSupplier() {
    if (this.newSupplierForm.invalid) {
      console.log('Invalid form controls:', this.findInvalidControls());
      return;
    }

    if (!this.data || this.data.isEditMode === undefined) {
      this.errorMessage = 'Missing data for edit mode check.';
      console.error('Data not provided correctly to dialog component.');
      return;
    }

    this.newSupplierForm.get('fuelSupplier')?.setErrors(null);

    const supplierDataRequest: AddFuelSupplierRequest = {
      name: this.newSupplierForm.value.fuelSupplier,
      stationManager: this.newSupplierForm.value.stationManager,
      sapId: this.newSupplierForm.value.sapId ? String(this.newSupplierForm.value.sapId) : '',
      address: this.newSupplierForm.value.address,
      remarks: this.newSupplierForm.value.remarks,
    };

    let supplierDataUpdate: UpdateFuelSupplierRequest | null = null;
    let contactsData: UpdateFuelSupplierContactRequest | null = null;

    if (this.data.isEditMode && this.data.fuelSupplierId !== undefined) {
      supplierDataUpdate = {
        id: this.data.fuelSupplierId,
        name: this.newSupplierForm.value.fuelSupplier,
        sapId: this.newSupplierForm.value.sapId ? String(this.newSupplierForm.value.sapId) : '',
        stationManager: this.newSupplierForm.value.stationManager,
        address: this.newSupplierForm.value.address,
        remarks: this.newSupplierForm.value.remarks,
      };

      contactsData = {
        fuelSupplierId: this.data.fuelSupplierId,
        contacts: this.mediaArray.value.map((contact: any) => ({
          id: contact.id,
          mediaNo: contact.mediaNo,
          mediaType: contact.mediaType,
          contactType: contact.contactType,
          contactDetails: contact.contactDetails,
        })),
      };
    }

    if (this.data.isEditMode && supplierDataUpdate && contactsData) {
      this.dataService.updateFuelSupplier(supplierDataUpdate).subscribe({
        next: () => {
          this.dataService.updateFuelSupplierContacts(contactsData!).subscribe({
            next: () => {
              if (this.contactsToDelete.length > 0) {
                this.dataService
                  .deleteFuelSupplierContacts({ ids: this.contactsToDelete })
                  .subscribe({
                    next: () => this.dialogRef.close(this.newSupplierForm.value),
                    error: (err) => {
                      console.error('Error deleting supplier contacts:', err);
                      this.errorMessage = 'Failed to delete supplier contacts.';
                    },
                  });
              } else {
                this.dialogRef.close(this.newSupplierForm.value);
              }
            },
            error: (err) => {
              if (err.error?.ErrorCode === 'FUEL_SUPPLIER_ALREADY_EXISTS') {
                this.newSupplierForm.get('fuelSupplier')?.setErrors({ supplierExists: true });
              } else {
                this.errorMessage = 'Failed to update supplier contacts.';
              }
            },
          });
        },
        error: (err) => {
          console.error('Error updating supplier:', err);
          if (err.error?.ErrorCode === 'FUEL_SUPPLIER_ALREADY_EXISTS') {
            this.newSupplierForm.get('fuelSupplier')?.setErrors({ supplierExists: true });
          } else {
            this.errorMessage = 'Failed to update supplier.';
          }
        },
      });
    } else {
      this.dataService.addFuelSupplier(supplierDataRequest).subscribe({
        next: (createdSupplier) => {
          contactsData = {
            fuelSupplierId: createdSupplier.id,
            contacts: this.mediaArray.value.map((contact: any) => ({
              id: contact.id,
              mediaNo: contact.mediaNo,
              mediaType: contact.mediaType,
              contactType: contact.contactType,
              contactDetails: contact.contactDetails,
            })),
          };

          this.dataService.updateFuelSupplierContacts(contactsData).subscribe({
            next: () => this.dialogRef.close(this.newSupplierForm.value),
            error: (err) => {
              console.error('Error saving supplier contacts:', err);
              if (err.error?.ErrorCode === 'FUEL_SUPPLIER_ALREADY_EXISTS') {
                this.newSupplierForm.get('fuelSupplier')?.setErrors({ supplierExists: true });
              } else {
                this.errorMessage = 'Failed to save supplier contacts.';
              }
            },
          });
        },
        error: (err) => {
          console.error('Error saving supplier:', err);
          if (err.error?.ErrorCode === 'FUEL_SUPPLIER_ALREADY_EXISTS') {
            this.newSupplierForm.get('fuelSupplier')?.setErrors({ supplierExists: true });
          } else {
            this.errorMessage = 'Failed to save supplier.';
          }
        },
      });
    }
  }


  onInput(event: Event, maxLength: number) {
    const input = event.target as HTMLInputElement;
    if (input.value.length > maxLength) {
      input.value = input.value.slice(0, maxLength);
    }
  }
}
